import { useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Close, Send, Image } from "@mui/icons-material";
import useAlert from "../../../../hooks/useAlert";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

const CreateTask = ({ open, onClose, onTaskCreated }) => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const { showErrorAlertAll } = useAlert();
  const userId = useSelector((state) => state?.auth?.user?.id) || "user1";


  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showErrorAlertAll("Dosya boyutu 5MB'dan büyük olamaz.");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const newAttachment = {
        id: Date.now(),
        name: file.name,
        type: file.type,
        data: reader.result,
      };
      setAttachments((prev) => [...prev, newAttachment]);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!title.trim() || (!content.trim() && attachments.length === 0)) return;

    try {
      setLoading(true);
      let finalContent = content;

      // Ekler varsa, içeriğin sonuna ekle
      if (attachments.length > 0) {
        finalContent += "\n\n";
        attachments.forEach((attachment) => {
          finalContent += `<img src="${attachment.data}" alt="Yüklenen görsel" />\n`;
        });
      }

      const taskData = {
        title: title.trim(),
        content: finalContent.trim(),
        userId: userId,
        userType:"user",
        ia: true,
        parentId: null, // Ana task olduğunu belirt
        sub_task: 0, // Ana task için sub_task 0 olmalı
      };

      // Task creation is handled by the parent component
      if (onTaskCreated) {
        await onTaskCreated(taskData);
        resetForm();
        handleClose();
      }
    } catch (error) {
      setLoading(false);
      showErrorAlertAll("Talep oluşturulurken bir hata oluştu.");
      console.error("Error creating task:", error);
    }
  };

  const resetForm = () => {
    setTitle("");
    setContent("");
    setAttachments([]);
    setLoading(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
          background: "linear-gradient(135deg, #F6F8FD 0%, #F1F4F9 100%)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            sx={{
              width: 5,
              height: 28,
              background: "linear-gradient(180deg, #2196F3 0%, #1976D2 100%)",
              borderRadius: "4px",
              mr: 2,
            }}
          />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Yeni Talep Oluştur
          </Typography>
        </Box>
        <IconButton onClick={handleClose}>
          <Close />
        </IconButton>
      </Box>

      {/* Form */}
      <DialogContent sx={{ p: 3 }}>
        <TextField
          fullWidth
          label="Başlık"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          margin="normal"
          required
          error={title === ""}
          helperText={title === "" ? "Başlık gereklidir" : ""}
          disabled={loading}
        />

        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            İçerik
          </Typography>

          <Box sx={{ border: "1px solid #ddd", borderRadius: 1, p: 1 }}>
            <TextField
              fullWidth
              multiline
              rows={10}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="İçerik yazın..."
              variant="outlined"
              disabled={loading}
              sx={{ "& .MuiOutlinedInput-root": { padding: 1 } }}
            />

            {/* Attachments Preview */}
            {attachments.length > 0 && (
              <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                {attachments.map((attachment, index) => (
                  <Box
                    key={attachment.id}
                    sx={{
                      position: "relative",
                      width: 100,
                      height: 100,
                      borderRadius: 1,
                      overflow: "hidden",
                      border: "1px solid rgba(0, 0, 0, 0.12)",
                    }}
                  >
                    <img
                      src={attachment.data}
                      alt={`Ek ${index + 1}`}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                    <IconButton
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        bgcolor: "rgba(255, 255, 255, 0.8)",
                        "&:hover": {
                          bgcolor: "rgba(255, 255, 255, 0.9)",
                        },
                      }}
                      onClick={() =>
                        setAttachments((prev) =>
                          prev.filter((a) => a.id !== attachment.id)
                        )
                      }
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}

            <Box sx={{ mt: 2, display: "flex", gap: 1, alignItems: "center" }}>
              <input
                type="file"
                accept="image/*"
                hidden
                ref={fileInputRef}
                onChange={handleImageUpload}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<Image />}
                onClick={() => fileInputRef.current?.click()}
                disabled={loading}
              >
                Resim Ekle
              </Button>
              {attachments.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {attachments.length} ek
                </Typography>
              )}
            </Box>
          </Box>
          {content === "" && attachments.length === 0 && (
            <Typography variant="caption" color="error">
              İçerik veya en az bir ek gereklidir
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button
            variant="contained"
            color="primary"
            endIcon={
              loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                <Send />
              )
            }
            onClick={handleSubmit}
            disabled={
              !title.trim() ||
              (!content.trim() && attachments.length === 0) ||
              loading
            }
          >
            Gönder
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

CreateTask.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onTaskCreated: PropTypes.func,
};

export default CreateTask;
